import React, { useEffect, useState } from "react";
import { getConsent, setConsent, getAnalyticsConfig } from "../lib/analytics";
import { L as Link } from "../lib/i18n";
import { Cookie, X, ShieldCheck, Settings2 } from "lucide-react";

const COPY = {
  de: {
    title: "Cookies & Datenschutz",
    body: "Wir nutzen technische Cookies für die Funktion der Plattform und optionale Analyse-Cookies (Google Analytics), um CarHive besser zu machen. Du entscheidest.",
    accept: "Alle akzeptieren",
    reject: "Nur Notwendige",
    settings: "Einstellungen",
    learnMore: "Datenschutz",
    detailsAnalytics: "Analyse-Cookies",
    detailsAnalyticsDesc: "Helfen uns zu verstehen, wie CarHive genutzt wird (Google Analytics 4, IP-Adresse anonymisiert).",
    save: "Auswahl speichern",
    necessary: "Notwendig",
    necessaryDesc: "Immer aktiv. Diese Cookies sind für die Funktion der Website essenziell.",
  },
  fr: {
    title: "Cookies & confidentialité",
    body: "Nous utilisons des cookies techniques nécessaires au fonctionnement et des cookies d'analyse facultatifs (Google Analytics) pour améliorer CarHive. À toi de choisir.",
    accept: "Tout accepter",
    reject: "Uniquement nécessaires",
    settings: "Paramètres",
    learnMore: "Confidentialité",
    detailsAnalytics: "Cookies d'analyse",
    detailsAnalyticsDesc: "Nous aident à comprendre l'usage de CarHive (Google Analytics 4, IP anonymisée).",
    save: "Enregistrer",
    necessary: "Nécessaires",
    necessaryDesc: "Toujours actifs. Essentiels au fonctionnement du site.",
  },
  it: {
    title: "Cookie & privacy",
    body: "Usiamo cookie tecnici per il funzionamento e cookie analitici facoltativi (Google Analytics) per migliorare CarHive. Decidi tu.",
    accept: "Accetta tutto",
    reject: "Solo necessari",
    settings: "Impostazioni",
    learnMore: "Privacy",
    detailsAnalytics: "Cookie analitici",
    detailsAnalyticsDesc: "Ci aiutano a capire come si usa CarHive (Google Analytics 4, IP anonimizzato).",
    save: "Salva",
    necessary: "Necessari",
    necessaryDesc: "Sempre attivi. Essenziali per il funzionamento del sito.",
  },
  en: {
    title: "Cookies & privacy",
    body: "We use necessary cookies for the platform and optional analytics cookies (Google Analytics) to make CarHive better. You decide.",
    accept: "Accept all",
    reject: "Only necessary",
    settings: "Settings",
    learnMore: "Privacy",
    detailsAnalytics: "Analytics cookies",
    detailsAnalyticsDesc: "Help us understand how CarHive is used (Google Analytics 4, IP anonymised).",
    save: "Save selection",
    necessary: "Necessary",
    necessaryDesc: "Always active. Essential for the site to work.",
  },
};

function getLocale() {
  // light-touch: read <html lang>; fallback to ch_lang
  if (typeof document !== "undefined" && document.documentElement?.lang) {
    const l = document.documentElement.lang;
    if (COPY[l]) return l;
  }
  try {
    const l = localStorage.getItem("ch_lang");
    if (COPY[l]) return l;
  } catch { /* ignore */ }
  return "de";
}

export default function CookieConsentBanner() {
  const [show, setShow] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(true);
  const cfg = getAnalyticsConfig();
  const c = COPY[getLocale()] || COPY.de;

  useEffect(() => {
    // Show banner only if not yet decided AND GA4 actually configured.
    // If GA4_ID isn't set, there's nothing meaningful to consent to.
    if (!cfg.enabled) { setShow(false); return; }
    const consent = getConsent();
    setShow(!consent.decided);
  }, [cfg.enabled]);

  if (!show) return null;

  const acceptAll = () => {
    setConsent({ analytics: true });
    setShow(false);
  };
  const rejectAll = () => {
    setConsent({ analytics: false });
    setShow(false);
  };
  const saveCustom = () => {
    setConsent({ analytics: analyticsOn });
    setShow(false);
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[100] px-3 pb-4 sm:bottom-4 sm:px-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-title"
      data-testid="consent-banner"
    >
      <div className="mx-auto max-w-3xl overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950/95 shadow-2xl shadow-black/40 backdrop-blur-xl">
        <div className="flex items-start gap-3 p-5 sm:p-6">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-red-600/15 text-red-400">
            <Cookie className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="consent-title" className="font-display text-base font-bold text-white sm:text-lg">
              {c.title}
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-zinc-400 sm:text-sm">
              {c.body}
            </p>

            {showSettings && (
              <div className="mt-4 space-y-3 rounded-xl border border-zinc-800 bg-black/40 p-4" data-testid="consent-settings">
                <SettingsRow
                  title={c.necessary}
                  desc={c.necessaryDesc}
                  checked
                  disabled
                  testid="consent-toggle-necessary"
                />
                <SettingsRow
                  title={c.detailsAnalytics}
                  desc={c.detailsAnalyticsDesc}
                  checked={analyticsOn}
                  onChange={setAnalyticsOn}
                  testid="consent-toggle-analytics"
                />
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-2">
              {!showSettings ? (
                <>
                  <button
                    onClick={acceptAll}
                    className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-red-600 to-orange-500 px-4 py-2 text-xs font-bold text-white shadow-lg shadow-red-900/30 transition hover:from-red-500 hover:to-orange-400"
                    data-testid="consent-accept-all"
                  >
                    <ShieldCheck className="h-3.5 w-3.5" /> {c.accept}
                  </button>
                  <button
                    onClick={rejectAll}
                    className="rounded-full border border-zinc-700 bg-transparent px-4 py-2 text-xs font-semibold text-zinc-300 transition hover:bg-zinc-900 hover:text-white"
                    data-testid="consent-reject-all"
                  >
                    {c.reject}
                  </button>
                  <button
                    onClick={() => setShowSettings(true)}
                    className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-zinc-400 transition hover:text-white"
                    data-testid="consent-open-settings"
                  >
                    <Settings2 className="h-3.5 w-3.5" /> {c.settings}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={saveCustom}
                    className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-4 py-2 text-xs font-bold text-white transition hover:bg-red-500"
                    data-testid="consent-save"
                  >
                    {c.save}
                  </button>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="rounded-full border border-zinc-800 bg-transparent px-3 py-2 text-xs font-semibold text-zinc-400 transition hover:text-white"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
              <Link
                to="/legal/privacy"
                className="ml-auto hidden text-[11px] text-zinc-500 underline-offset-2 hover:text-zinc-300 hover:underline sm:inline"
                data-testid="consent-privacy-link"
              >
                {c.learnMore}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SettingsRow({ title, desc, checked, onChange, disabled, testid }) {
  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={title}
        disabled={disabled}
        onClick={() => !disabled && onChange?.(!checked)}
        data-testid={testid}
        className={`mt-0.5 inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition ${
          checked ? "bg-emerald-500" : "bg-zinc-700"
        } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <span
          className={`block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
            checked ? "translate-x-4" : "translate-x-0.5"
          }`}
        />
      </button>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-white">{title}</p>
        <p className="mt-0.5 text-[11px] leading-relaxed text-zinc-400">{desc}</p>
      </div>
    </div>
  );
}
